/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import { createSlice, createAction } from '@reduxjs/toolkit';
import { put, takeLatest } from 'redux-saga/effects';
import { BridgeClient, BRIDGE_ERROR_CODES } from 'app/service/bridge/BridgeClient';
import { enqueueErrorSnackbar } from '../actions/Notifications';
import { getLocalizedMessage } from '../util/useLocalizedNotifications';
import { getLocale } from '../sagas/utils/localeSelectors';

const initialState = {
  details: {
    isLoaded: false,
  },
};
let bridgeClient = null;
function getBridgeClient() {
  if (!bridgeClient) bridgeClient = new BridgeClient();
  return bridgeClient;
}

function getCloudRegistrationClient() {
  // Keep the Cloud API client out of the shared /embedded/app bootstrap path.
  // It is needed only after the legacy marketplace bridge explicitly asks the
  // browser to complete registration.
  const { wsClient } = require('util/appUtils'); // eslint-disable-line global-require
  return wsClient;
}
const sliceName = 'bridge';

export const BRIDGE_ERROR = `${sliceName}/BRIDGE_ERROR`;

export const bridgeGetDetails = createAction(`${sliceName}/bridgeGetDetails`);
export const bridgeRegister = createAction(`${sliceName}/bridgeRegister`);
export const bridgeSetTokens = createAction(`${sliceName}/bridgeSetTokens`);
export const bridgeClearTokens = createAction(`${sliceName}/bridgeClearTokens`);
export const bridgeBillingOpenBilling = createAction(`${sliceName}/bridgeBillingOpenBilling`);
export const bridgeGetFinalUrl = createAction(`${sliceName}/bridgeGetFinalUrl`);
export const bridgeLogout = createAction(`${sliceName}/bridgeLogout`);

const bridgeSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    bridgeDetailsChange(state, { payload }) {
      return {
        ...state,
        details: {
          ...state.details,
          ...payload,
          isLoaded: true,
        },
        errorNonce: 0,
      };
    },
    bridgeRegisterChange(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    bridgeSetTokensChange(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  extraReducers: {
    [BRIDGE_ERROR]: (state) => {
      state.errorNonce = (state.errorNonce || 0) + 1;
    },
  },
});

export const { actions, reducer } = bridgeSlice;
export const {
  bridgeDetailsChange,
  bridgeRegisterChange,
  bridgeSetTokensChange,
} = actions;

export default reducer;

function buildCall(actionFunction) {
  return function* call(action = {}) {
    try {
      const response = yield actionFunction(action.payload);
      yield put(bridgeDetailsChange(response || {}));
    } catch (err) {
      yield put({ type: BRIDGE_ERROR, payload: err });
    }
  };
}

const ERROR_VISIBILITY_TIME = 30000;

function* errorHandler({ payload: error }) {
  const locale = yield* getLocale();

  if (error instanceof Error) {
    const message = getLocalizedMessage('notifications.error.bridgeError', { error: error.message }, locale);
    return yield put(enqueueErrorSnackbar(message, ERROR_VISIBILITY_TIME));
  }
  const { errorCode, message, error: errorMessage } = error;
  if (errorCode === BRIDGE_ERROR_CODES.NOT_IN_IFRAME) {
    const msg = getLocalizedMessage('notifications.error.communicationNotEmbedded', {}, locale);
    return yield put(enqueueErrorSnackbar(msg, ERROR_VISIBILITY_TIME));
  }

  if (errorCode === BRIDGE_ERROR_CODES.TIMEOUT) {
    const msg = getLocalizedMessage('notifications.error.communicationTimeout', {}, locale);
    return yield put(enqueueErrorSnackbar(msg, ERROR_VISIBILITY_TIME));
  }

  const messageText = message || errorMessage || JSON.stringify(error);
  const msg = getLocalizedMessage('notifications.error.communicationError', { message: messageText }, locale);
  return yield put(enqueueErrorSnackbar(msg, ERROR_VISIBILITY_TIME));
}

export function* sagas() {
  yield takeLatest(
    bridgeGetDetails().type,
    buildCall((payload) => getBridgeClient().getDetails(payload)),
  );
  yield takeLatest(
    bridgeRegister().type,
    buildCall(async (payload) => {
      const result = await getBridgeClient().forceRegister(payload);

      // Marketplace PrestaShop 1.0.5 opens the legacy /embedded/signin route.
      // Its bridge only prepares registration data, so the Cloud client must
      // create the shop and persist the returned credentials in the plugin.
      // The self-hosted /embedded/app bootstrap does not dispatch this action;
      // it owns registration through installation.register instead.
      if (result.needsClientRegistration && result.registrationData) {
        const { platform, appId, ...registrationData } = result.registrationData;
        const { shop } = await getCloudRegistrationClient().registerShop(
          platform,
          appId,
          registrationData,
        );
        await getBridgeClient().setTokens(shop.token, shop.secret, shop.shopId);
      }

      getBridgeClient().forceReloadPage();
      return result;
    }),
  );
  yield takeLatest(
    bridgeSetTokens().type,
    buildCall(async (payload) => {
      const { token, secret } = payload;
      const result = await getBridgeClient().setTokens(token, secret);
      getBridgeClient().forceReloadPage();
      return result;
    }),
  );
  yield takeLatest(
    bridgeClearTokens().type,
    buildCall(async () => {
      const result = await getBridgeClient().clearTokens();
      getBridgeClient().forceReloadPage();
      return result;
    }),
  );
  yield takeLatest(
    bridgeBillingOpenBilling().type,
    buildCall((payload) => getBridgeClient().openBillingPage(payload)),
  );
  yield takeLatest(
    bridgeGetFinalUrl().type,
    function* getFinalUrl(action) {
      try {
        const response = yield getBridgeClient().getFinalUrl(action.payload);
        // Return response without changing bridge details state
        // The caller will handle the response
        return response;
      } catch (err) {
        yield put({ type: BRIDGE_ERROR, payload: err });
        throw err;
      }
    },
  );
  yield takeLatest(
    bridgeLogout().type,
    buildCall(() => getBridgeClient().logout()),
  );
  yield takeLatest(BRIDGE_ERROR, errorHandler);
}

export const forceReloadPage = () => getBridgeClient().forceReloadPage();
